/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Home
 * File     : Home.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Home view and coordinating data fetching.
 *   Exports: Home
 *   
 *
 * Dependencies:
 *   - ../components/layout/Ads
 *   - ../context/AuthContext
 *   - ../services/api
 *   - @mui/icons-material/Delete
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/home" element={<Home />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { Typography, Box, Grid, Card, CardContent, Button, Chip, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdSlot } from '../components/layout/Ads';
import { fetchPolls, deletePoll } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const [polls, setPolls] = useState([]);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchPolls({ active: true, limit: 6 }).then(data => setPolls(data.polls || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const formatRemaining = (endDateStr) => {
    const end = new Date(endDateStr).getTime();
    let diff = Math.max(0, end - now);
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    diff -= days * 24 * 60 * 60 * 1000;
    const hours = Math.floor(diff / (60 * 60 * 1000));
    diff -= hours * 60 * 60 * 1000;
    const minutes = Math.floor(diff / (60 * 1000));
    diff -= minutes * 60 * 1000;
    const seconds = Math.floor(diff / 1000);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const handleDelete = async (id) => {
    if (!user) return;
    if (!window.confirm('Delete this active poll?')) return;
    try {
      await deletePoll(id);
      setPolls(prev => prev.filter(p => p.id !== id));
    } catch (_) {}
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>What is ReelConsensus?</Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          ReelConsensus is a collaborative way to decide what to watch. Create polls, invite friends, and see votes update in real‑time.
          After the poll ends, the winner joins your shared history where everyone can rate it and discuss the results.
          With Discord login and webhook alerts, Plex library integration, Watchmode streaming links, and platform‑wide metrics,
          your club has everything it needs to plan the next movie night.
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.95, mt: 1 }}>
          New: explore platform‑wide insights on the <strong>Global Metrics</strong> page — see top poll creators, the top‑rated winning films,
          a genre breakdown of winners, a weekly roundup with quick links to results, and a running graph of polls created.
        </Typography>
        <Typography variant="h6" sx={{ mt: 2 }}>Free Features</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          • Public polls with real‑time updates and countdown timers.<br/>
          • Results & history with automatic winner capture and timestamps.<br/>
          • Post‑view ratings once the winner’s runtime elapses, with live countdowns on Results and Rating Opportunities (half‑star ratings supported; one‑time and voter‑only).<br/>
          • Previously Watched: browse past winners with posters and aggregates.<br/>
          • OMDb details on titles: runtime, genres, ratings, plot, cast, director.<br/>
          • Global Metrics (read‑only): top creators, genres, and trends.<br/>
          • Smart reminders: toasts notify you when ratings open (never early).<br/>
          • Secure sign‑in with email verification (TOTP) for new accounts.<br/>
          • Image proxy and caching for fast, reliable thumbnails.<br/>
          • Discord login for quick sign‑in.<br/>
          • Public polls labeled automatically without creating a Club.<br/>
        </Typography>
        <Typography variant="h6" sx={{ mt: 2 }}>Host Plan (Paid)</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          • Clubs (private spaces): create invite‑only communities and run club‑only polls.<br/>
          • Club Discussions: comment threads on poll results for members.<br/>
          • View Owner's Plex Library (no personal Plex integration on Host). Movies are not viewable on ReelConsensus; Plex is used only to surface titles for creating and voting on polls.<br/>
          • Random Poll Generator: auto‑build polls from your Plex library (by count/genre/actor/director).<br/>
          • Where to Watch (Watchmode): streaming buttons with provider links.<br/>
          • Discord Alerts: per‑club webhook notifications when you create polls.<br/>
          • Global Metrics (enhanced): personal series view (Me vs All) and weekly roundups across your clubs (includes Public).<br/>
          • Club Streamer: Multi-platform video streaming (YouTube, Twitch TV, Kick) with synchronized chat and playlist management for club members.<br/>
          • Advanced Spam Prevention: Rate limiting, message similarity detection, and user behavior monitoring for Club Streamer chat.<br/>
          • Member Management: Add, remove, and manage club members with role-based permissions and display name support.<br/>
          • Search Owner's Physical Media Collection: Browse and search the Platform Owner's personal movie collection during poll creation.<br/>
          • Club limit: create up to 5 clubs; add‑on tiers available for more capacity.<br/>
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          • Note: Personal Plex integration is available on the Self‑Hosted plan. ReelConsensus does not host or stream media; we do not support piracy.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Clubs whose Host subscription is inactive are visibly marked and locked from new poll creation until renewal.
        </Typography>
        <Typography variant="h6" sx={{ mt: 2 }}>Add‑on Tiers</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          • Tier 1: total up to 9 clubs (Host gives 5; Tier 1 adds 4).<br/>
          • Tier 2: total up to 19 clubs.<br/>
          • Tier 3: total up to 29 clubs.<br/>
          • Add‑ons are non‑stacking and can be changed anytime (downgrades require staying under the target limit).<br/>
        </Typography>
        <Typography variant="h6" sx={{ mt: 2 }}>Owner Tools</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          • Platform‑wide administration and guardrails (Admin Panel link only for Admin/Owner).<br/>
          • Manage all Clubs and membership (add, promote, remove—including self/owner—, delete empty clubs).<br/>
          • Database tools: export/import JSON from the Admin Panel.<br/>
          • GDPR Wipe: delete a user's comments, Club Streamer chat messages, anonymize votes, and reassign clubs/polls to Owner.<br/>
          • Subscription Management: adjust user plan/status (audited in real‑time).<br/>
          • Advanced chat optimization with message compression and automated cleanup.<br/>
          • Spam detection statistics and user behavior monitoring.<br/>
          • Physical Media Collection: Import your movie collection from external sources, manage physical media with barcode scanning, and search your collection during poll creation.<br/>
          • Collection Migration Tool: Seamlessly import your entire movie collection with metadata, images, and purchase details.<br/>
          • Physical Media Collection Management: Access to Collection Migration tools and collection management features.<br/>
          • Security: source maps blocked in production; usernames and emails normalized to lowercase.<br/>
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
          Create a Club to keep your movie nights organized, private, and fun. Owners and Admins can manage polls and users,
          while members enjoy a streamlined voting experience, rich movie details, and post‑view discussions.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Manage your subscription and tier from your Profile. If a Host subscription becomes inactive, existing clubs remain visible
          but new poll creation is locked until resubscribed.
        </Typography>
      </Box>
      <Typography variant="h4" gutterBottom>Active Polls</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AdSlot slot="2158987682" />
        </Grid>
        {polls.filter(p => p && p.id).map((p) => (
          <Grid item xs={12} md={6} lg={4} key={p.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{p.title}</Typography>
                {p.club ? (
                  <Typography variant="caption" color="text.secondary">Club: {p.club.name}</Typography>
                ) : (p.clubNameArchived ? (
                  <Typography variant="caption" color="text.secondary">{p.clubNameArchived}</Typography>
                ) : null)}
                <Typography variant="body2" color="text.secondary">{p.description}</Typography>
                {p.endDate && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" color="secondary" label={`Ends in ${formatRemaining(p.endDate)}`} />
                  </Box>
                )}
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button onClick={() => navigate(`/results/${p.id}`)}>View</Button>
                  {(user?.role === 'owner' || user?.role === 'admin') && (
                    <Tooltip title="Delete poll">
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12}>
          <AdSlot slot="9845906013" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;


